import {
    type BackgroundColorValue,
    Button,
    type ButtonProps,
    Div,
    Flex,
    type FlexProps,
    Inline,
    Text
} from "@hopper-ui/components";

import { DismissIcon, RemoveIcon, SuccessIcon } from "@hopper-ui/icons";
import { type FocusEvent, type Ref, useCallback, useEffect, useRef } from "react";

import { useComponentsTranslation } from "../i18n/i18next.ts";
import { useCommittedRef } from "../utils/useCommittedRef.ts";
import styles from "./Toast.module.css";

export interface ToastAction {
    label: string;
    onPress: () => void;
}

export interface ToastProps extends Omit<FlexProps, "children"> {
    variant: "success" | "error";
    message: string;
    action?: ToastAction;
    durationInMs?: number;
    size?: "normal" | "wide";
    onClose: () => void;
    titleProps?: Omit<FlexProps, "children">;
    closeButtonProps?: Partial<Omit<ButtonProps, "onPress" | "children">>;
    ref?: Ref<HTMLDivElement>;
}

export const Toast = (props: ToastProps) => {
    const {
        variant,
        message,
        action,
        durationInMs,
        size = "normal",
        onClose,
        titleProps = {},
        closeButtonProps: { "aria-label": ariaLabel, ...closeButtonProps } = {},
        ...rest
    } = props;
    const { t } = useComponentsTranslation();

    // Using a ref instead of the provided onClose handler to prevent re-starting animation on re-renders.
    const onCloseRef = useCommittedRef(onClose);

    const progressBarRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<Animation | null>(null);

    const close = useCallback(() => {
        onCloseRef.current!();
    }, [onCloseRef]);

    useEffect(() => {
        const element = progressBarRef.current;

        if (element && durationInMs) {
            const animation = element.animate([{ width: "100%" }, { width: "0%" }], {
                duration: durationInMs,
                easing: "linear"
            });

            animationRef.current = animation;

            animation.onfinish = () => {
                close();
            };

            return () => {
                animationRef.current = null;
                animation.cancel();
            };
        }
    }, [durationInMs, close]);

    const isHoveredRef = useRef(false);
    const isFocusedRef = useRef(false);

    const maybeResume = useCallback(() => {
        if (!isHoveredRef.current && !isFocusedRef.current) {
            animationRef.current?.play();
        }
    }, []);

    const handlePointerEnter = useCallback(() => {
        isHoveredRef.current = true;
        animationRef.current?.pause();
    }, []);

    const handlePointerLeave = useCallback(() => {
        isHoveredRef.current = false;
        maybeResume();
    }, [maybeResume]);

    // Guarded against relatedTarget being inside the toast: without it, tabbing between the toast's own
    // controls (e.g. the action link to the Dismiss button) fires blur then focus on the container, briefly
    // resuming the animation in between.
    const handleFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
        if (event.currentTarget.contains(event.relatedTarget)) {
            return;
        }

        isFocusedRef.current = true;
        animationRef.current?.pause();
    }, []);

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            if (event.currentTarget.contains(event.relatedTarget)) {
                return;
            }

            isFocusedRef.current = false;
            maybeResume();
        },
        [maybeResume]
    );

    const handleCloseButtonClick = useCallback(() => {
        close();
    }, [close]);

    const handleActionClick = useCallback(() => {
        action?.onPress();
        close();
    }, [action, close]);

    const iconMarkup = {
        success: <SuccessIcon fill="success-weakest" aria-hidden />,
        error: <RemoveIcon fill="danger-weak" aria-hidden />
    }[variant];

    const backgroundColor = {
        success: "success-strong",
        error: "danger-strong"
    }[variant] as BackgroundColorValue;

    const textMarkup = (
        <Text
            color="neutral-always-light"
            role="status"
            aria-live={durationInMs ? "assertive" : "polite"}
            textOverflow="ellipsis"
            overflow="hidden"
            minWidth={0}
            className={styles.message}
        >
            {message}
        </Text>
    );

    const actionMarkup = action && (
        <Button onPress={handleActionClick} variant="ghost-primary" size="sm">
            {action.label}
        </Button>
    );

    const closeButtonMarkup = (
        <Button
            {...closeButtonProps}
            aria-label={ariaLabel ?? t("Toast.dismiss")}
            variant="ghost-secondary"
            backgroundColor="transparent"
            size="sm"
            UNSAFE_margin="-0.5rem"
            onPress={handleCloseButtonClick}
        >
            <DismissIcon fill="neutral-always-light" />
        </Button>
    );

    const progressBarMarkup = durationInMs && (
        <Div
            ref={progressBarRef}
            position="absolute"
            bottom={0}
            left={0}
            height="core_40"
            backgroundColor={backgroundColor}
        />
    );

    return (
        <Flex
            position="relative"
            gap="inline-sm"
            alignItems="center"
            borderRadius={{ base: 0, xs: "core_2-5" }}
            padding="core_160"
            backgroundColor="neutral-always-dark"
            UNSAFE_width="100%"
            UNSAFE_maxWidth={{
                xs: size === "wide" ? "800px" : "488px"
            }}
            justifyContent="space-between"
            overflow="hidden"
            // Required to prevent breaking the animation. See ToastRegion pointerEvents="none".
            pointerEvents="initial"
            UNSAFE_boxShadow="0 4px 10px 4px rgba(0, 0, 0, 0.08), 0 8px 24px 0 rgba(0, 0, 0, 0.12)"
            style={{ backdropFilter: "blur(7.5px)" }}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...rest}
        >
            <Flex gap="inline-sm" alignItems="center" overflow="hidden" minWidth={0} {...titleProps}>
                {iconMarkup}
                {textMarkup}
            </Flex>
            <Inline gap="inline-md" alignY="center" shrink={0}>
                {actionMarkup}
                {closeButtonMarkup}
            </Inline>
            {progressBarMarkup}
        </Flex>
    );
};
